import React, { useEffect, useRef, useState } from "react";
import "./Navbar.css";
import logo from "../../../assets/images/fullLogo.svg";
import darkThemeLogo from "../../../assets/images/darkthemelogo.svg";
import hamburgerMenu from "../../../assets/icons/hamburgerMenu.svg";
import { FiChevronDown, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import AboutMegaMenu from "./AboutMegaMenu/AboutMegaMenu";
import MobileNav from "./MobileNav/MobileNav";
import RequestCallModal from "../RequestCallModal/RequestCallModal";
import LangLink from "../LangLink";
import { useSwitchLanguage } from "../../../hooks/useLanguageRouting";
import { normalizeLanguage } from "../../../utils/languageRouting";

const Navbar = ({ theme = "light" }) => {
  const { t, i18n } = useTranslation();
  const switchLanguage = useSwitchLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isRequestCallOpen, setIsRequestCallOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const languageRef = useRef(null);
  const activeLang = normalizeLanguage(i18n.language);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 992) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!languageRef.current?.contains(event.target)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 2);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };
  const isScrolledTheme = isScrolled || theme === "dark";

  return (
    <div className={`navbar ${isScrolled ? "navbar--glassed" : ""} ${isScrolledTheme ? "navbar--darkLinks" : ""}`}>
      <div className="navbar-container">
        <LangLink to="/" className="navbar-left" onClick={closeMobileMenu}>
          <img
            className="logo"
            src={isScrolledTheme ? darkThemeLogo : logo}
            alt="Logo"
          />
        </LangLink>

        <button
          className="mobile-menu-toggle"
          type="button"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
          onClick={toggleMobileMenu}
        >
          <img src={hamburgerMenu} alt="" aria-hidden="true" />
        </button>

        <div className="navbar-center">
          <ul className="nav-links">
            <li className="nav-item--mega">
              <LangLink to="/about">
                {t("navbar.aboutUs")} <FiChevronDown className="nav-icon" />
              </LangLink>
              <AboutMegaMenu />
            </li>

            <li>
              <LangLink to="/products">
                {t("navbar.products")}
              </LangLink>
            </li>

            <li>
              <LangLink to="/allsolutions">
                {t("navbar.solutions")}
              </LangLink>
            </li>

            <li>
              <LangLink to="/services">
                {t("navbar.services")}
              </LangLink>
            </li>

            <li>
              <LangLink to="/sectors">
                {t("navbar.sectors")}
              </LangLink>
            </li>

            <li>
              <LangLink to="/allprojects">{t("navbar.projects")}</LangLink>
            </li>

            <li>
              <LangLink to="/estimator">
                {t("navbar.estimator")}
              </LangLink>
            </li>
          </ul>

          <div className="navdivider"></div>
          <div className="navbar-right">
            <div className="language language-dropdown" ref={languageRef}>
              <button
                type="button"
                className="language-trigger"
                onClick={() => setIsLangOpen((prev) => !prev)}
              >
                {activeLang.toUpperCase()} <FiChevronDown className="nav-icon" />
              </button>
              {isLangOpen ? (
                <div className="language-menu">
                  {[
                    { code: "en", labelKey: "langEn" },
                    { code: "ar", labelKey: "langAr" },
                    { code: "ku", labelKey: "langKu" },
                  ].map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      className={`language-menu-item ${activeLang === lang.code ? "is-active" : ""}`}
                      onClick={() => {
                        switchLanguage(lang.code);
                        setIsLangOpen(false);
                      }}
                    >
                      {t(`navbar.${lang.labelKey}`)}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <button
              className="buttongyp"
              type="button"
              onClick={() => setIsRequestCallOpen(true)}
            >
              {t("navbar.requestCall")}
            </button>
          </div>
        </div>

        <MobileNav
          isOpen={isMobileMenuOpen}
          onClose={closeMobileMenu}
          t={t}
          i18n={i18n}
          onRequestCall={() => {
            closeMobileMenu();
            setIsRequestCallOpen(true);
          }}
        />
      </div>
      <RequestCallModal
        isOpen={isRequestCallOpen}
        onClose={() => setIsRequestCallOpen(false)}
      />
    </div>
  );
};

export default Navbar;